import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type TimerStatus = 'idle' | 'running' | 'paused' | 'break'

interface PomodoroState {
  // Timer state
  status: TimerStatus
  timeRemaining: number // in seconds
  isWorkSession: boolean
  sessionsCompleted: number
  totalSessionsToday: number

  // Settings
  workDuration: number // in minutes
  breakDuration: number // in minutes
  longBreakDuration: number
  sessionsBeforeLongBreak: number

  // Stats
  todayMinutes: number
  lastActiveDate: string | null

  // Actions
  startTimer: () => void
  pauseTimer: () => void
  resumeTimer: () => void
  resetTimer: () => void
  tick: () => boolean // returns true if timer completed
  startBreak: () => void
  skipBreak: () => void
  completeSession: () => void
  setWorkDuration: (minutes: number) => void
  setBreakDuration: (minutes: number) => void
}

export const usePomodoroStore = create<PomodoroState>()(
  persist(
    (set, get) => ({
      status: 'idle',
      timeRemaining: 25 * 60,
      isWorkSession: true,
      sessionsCompleted: 0,
      totalSessionsToday: 0,

      workDuration: 25,
      breakDuration: 5,
      longBreakDuration: 15,
      sessionsBeforeLongBreak: 4,

      todayMinutes: 0,
      lastActiveDate: null,

      startTimer: () => {
        const state = get()
        const today = new Date().toISOString().split('T')[0]

        // Reset daily stats if new day
        if (state.lastActiveDate !== today) {
          set({
            todayMinutes: 0,
            totalSessionsToday: 0,
            lastActiveDate: today,
          })
        }

        set({
          status: 'running',
          timeRemaining: state.workDuration * 60,
          isWorkSession: true,
        })
      },

      pauseTimer: () => set({ status: 'paused' }),

      resumeTimer: () => set({ status: 'running' }),

      resetTimer: () => {
        const state = get()
        set({
          status: 'idle',
          timeRemaining: state.workDuration * 60,
          isWorkSession: true,
        })
      },

      tick: () => {
        const state = get()
        if (state.status !== 'running') return false

        const newTime = state.timeRemaining - 1

        if (newTime <= 0) {
          // Timer completed
          if (state.isWorkSession) {
            // Work session completed
            set({
              status: 'idle',
              timeRemaining: 0,
            })
            return true // Signal completion
          } else {
            // Break completed
            set({
              status: 'idle',
              timeRemaining: state.workDuration * 60,
              isWorkSession: true,
            })
            return false
          }
        }

        set({ timeRemaining: newTime })
        return false
      },

      startBreak: () => {
        const state = get()
        const isLongBreak = (state.sessionsCompleted + 1) % state.sessionsBeforeLongBreak === 0
        const breakTime = isLongBreak ? state.longBreakDuration : state.breakDuration

        set({
          status: 'running',
          timeRemaining: breakTime * 60,
          isWorkSession: false,
        })
      },

      skipBreak: () => {
        const state = get()
        set({
          status: 'idle',
          timeRemaining: state.workDuration * 60,
          isWorkSession: true,
        })
      },

      completeSession: () => {
        const state = get()
        set({
          sessionsCompleted: state.sessionsCompleted + 1,
          totalSessionsToday: state.totalSessionsToday + 1,
          todayMinutes: state.todayMinutes + state.workDuration,
        })
      },

      setWorkDuration: (minutes) => {
        set({
          workDuration: minutes,
          timeRemaining: minutes * 60,
        })
      },

      setBreakDuration: (minutes) => set({ breakDuration: minutes }),
    }),
    {
      name: 'waifudo-pomodoro',
      partialize: (state) => ({
        workDuration: state.workDuration,
        breakDuration: state.breakDuration,
        longBreakDuration: state.longBreakDuration,
        sessionsBeforeLongBreak: state.sessionsBeforeLongBreak,
        todayMinutes: state.todayMinutes,
        totalSessionsToday: state.totalSessionsToday,
        lastActiveDate: state.lastActiveDate,
      }),
    }
  )
)
